/**
 * Custom hook to manage wallet-related operations and state.
 * Provides functionality for fetching wallet transactions, handling modals,
 * and submitting wallet transactions.
 */
import {useState, useRef} from 'react';
import API from '../network/dashboardAPI';

export const useWallet = () => {
	// State variables for wallet operations
	const [isWalletRequesting, setIsWalletRequesting] = useState(false); // Indicates if wallet transactions are being fetched
	const [walletTransactions, setWalletTransactions] = useState([]); // Stores wallet transactions
	const [nextOffset, setNextOffset] = useState(null); // Tracks pagination offset for transactions
	const [walletInfo, setWalletInfo] = useState(null); // Stores wallet information
	const [isShowWalletTransactionModal, setIsShowWalletTransactionModal] = useState(false); // Controls visibility of wallet transaction modal
	const [isShowAddWalletTranModal, setIsShowAddWalletTranModal] = useState(false); // Controls visibility of add wallet transaction modal
	const [isShowUpdateWalletTranModal, setIsShowUpdateWalletTranModal] = useState(false); // Controls visibility of update wallet transaction modal
	const [customerToViewWallet, setCustomerToViewWallet] = useState({}); // Stores customer data for viewing wallet transactions
	const [editCustomer, setEditCustomer] = useState({}); // Stores customer data for editing wallet transactions
	const [selectedTransaction, setSelectedTransaction] = useState(null); // Stores transaction data for updating
	const [walletFormError, setWalletFormError] = useState({
		reason: false,
		amount: false,
		transactionType: false,
	}); // Tracks form validation errors

	// State variables for wallet transaction form
	const [reason, setReason] = useState("refund"); // Reason for the wallet transaction
	const [amount, setAmount] = useState(0); // Amount for the wallet transaction
	const [comment, setComment] = useState(""); // Comment for the wallet transaction
	const [orderNumber, setOrderNumber] = useState(""); // Comment for the wallet transaction
	const [transactionType, setTransactionType] = useState("credit"); // Type of transaction (credit/debit)
	const [isRequestingUpsert, setIsRequestingUpsert] = useState(false); // Indicates if a wallet transaction is being submitted

	// Ref for text search input
	const _textSearch = useRef();

	/**
	 * Fetches wallet transactions for a specific customer.
	 * @param {Object} customer - Customer object containing `cust_id`.
	 * @param {number} [next=0] - Pagination offset for fetching transactions.
	 * @param {number} [limit=15] - Number of transactions to fetch.
	 * @param {boolean} [append=false] - Whether to append transactions to the existing list.
	 */
	const getWalletTransactionByCustomerId = async (customer, next = 0, limit = 15, append = false) => {
		try {
			setIsWalletRequesting(true);
			const response = await API.getWalletTransactionByCustomerId({
				customerId: customer.cust_id,
				next: next,
				limit: limit
			});

			const wallet = await response.json();
			const {walletInfo, list, next: nextOffset} = wallet.data;

			setWalletInfo(walletInfo);
			setNextOffset(nextOffset);
			setWalletTransactions(prev => (
				append ? [...(Array.isArray(prev) ? prev : []), ...list] : list
			));
		} catch (error) {
			console.error("Error fetching wallet transactions:", error);
			setWalletTransactions([]);
			setIsShowWalletTransactionModal(false);
		} finally {
			setIsWalletRequesting(false);
		}
	};

	/**
	 * Opens the wallet transaction modal for a specific customer.
	 * @param {Object} customer - Customer object to view wallet transactions.
	 */
	const handleViewWalletTransaction = (customer) => {
		setCustomerToViewWallet(customer);
		setIsShowWalletTransactionModal(true);
		getWalletTransactionByCustomerId(customer);
	};

	/**
	 * Loads more wallet transactions for the current customer.
	 */
	const handleLoadMoreTransactions = () => {
		if (nextOffset !== null) {
			getWalletTransactionByCustomerId(customerToViewWallet, nextOffset, 10, true);
		}
	};

	/**
	 * Closes the wallet transaction modal and resets related state.
	 */
	const handleCloseWalletTransactionModal = () => {
		setWalletTransactions([]);
		setWalletInfo(null);
		setCustomerToViewWallet({});
		setIsShowWalletTransactionModal(false);
	};

	/**
	 * Opens the add wallet transaction modal for a specific customer.
	 * @param {Object} customer - Customer object to add a wallet transaction.
	 */
	const handleOpenAddWalletTransactionModal = (customer) => {
		setIsShowAddWalletTranModal(true);
		setEditCustomer(customer);
	};

	/**
	 * Closes the add wallet transaction modal and resets related state.
	 */
	const handleCloseAddWalletTransactionModal = () => {
		setIsShowAddWalletTranModal(false);
		setEditCustomer({});
		resetWalletFormData();
		setWalletFormError({
			reason: false,
			amount: false,
			transactionType: false,
		});
	};

	/**
	 * Resets the wallet transaction form data to default values.
	 */
	const resetWalletFormData = () => {
		setAmount(0);
		setReason("refund");
		setComment("");
		setTransactionType("credit");
	};

	/**
	 * Submits a new wallet transaction for the current customer.
	 */
	const handleSubmitAddWalletTransaction = async () => {
		setIsRequestingUpsert(true);

		if (reason && amount > 0) {
			const body = {
				transaction_type: transactionType,
				wallet_owner_id: editCustomer.cust_id,
				reason,
				wallet_amount: Number(amount),
				comment,
				order_number: orderNumber?.value || null
			};

			try {
				const response = await API.addWalletTransaction(body).then(res => res.json());

				setIsRequestingUpsert(false);

				if (response.success) {
					_textSearch.current && (_textSearch.current.value = "");
					handleCloseAddWalletTransactionModal();
				} else {
					alert(response.message)
				}
			} catch (error) {
				setIsRequestingUpsert(false);
				alert(error.message)
				console.error("Error adding wallet transaction:", error);
			}
		} else {
			if (!reason) {
				setWalletFormError(prev => ({...prev, reason: true}));
			}

			if (!amount || amount <= 0) {
				setWalletFormError(prev => ({...prev, amount: true}));
			}

			setIsRequestingUpsert(false);
		}
	};

	/**
	 * Opens the update wallet transaction modal for a specific transaction.
	 * @param {Object} transaction - Transaction object to update.
	 */
	const handleOpenUpdateWalletTransactionModal = (transaction) => {
		setSelectedTransaction(transaction);
		setOrderNumber(transaction.order_number ? {
			value: transaction.order_number,
			label: transaction.order_number
		} : null);
		setIsShowUpdateWalletTranModal(true);
	};

	/**
	 * Closes the update wallet transaction modal and resets related state.
	 */
	const handleCloseUpdateWalletTransactionModal = () => {
		setIsShowUpdateWalletTranModal(false);
		setSelectedTransaction(null);
		setOrderNumber(null);
	};

	/**
	 * Submits an update to an existing wallet transaction.
	 */
	const handleSubmitUpdateWalletTransaction = async () => {
		setIsRequestingUpsert(true);

		try {
			const body = {
				order_number: orderNumber?.value || null
			};

			const response = await API.updateWalletTransaction(selectedTransaction.id, body).then(res => res.json());

			setIsRequestingUpsert(false);

			if (response.success) {
				// Refresh the wallet transactions list
				await getWalletTransactionByCustomerId(customerToViewWallet);
				handleCloseUpdateWalletTransactionModal();
			} else {
				alert(response.message || 'Failed to update transaction');
			}
		} catch (error) {
			setIsRequestingUpsert(false);
			alert(error.message || 'Failed to update transaction');
			console.error("Error updating wallet transaction:", error);
		}
	};

	return {
		// State variables
		isWalletRequesting,
		isRequestingUpsert,
		walletTransactions,
		walletInfo,
		nextOffset,
		isShowWalletTransactionModal,
		isShowAddWalletTranModal,
		isShowUpdateWalletTranModal,
		customerToViewWallet,
		editCustomer,
		selectedTransaction,
		walletFormError,
		reason, amount, comment, transactionType,
		_textSearch,
		orderNumber,
		// State setters
		setReason, setAmount, setComment, setTransactionType, setOrderNumber,

		// Handlers
		handleViewWalletTransaction,
		handleCloseWalletTransactionModal,
		handleLoadMoreTransactions,
		handleOpenAddWalletTransactionModal,
		handleCloseAddWalletTransactionModal,
		handleSubmitAddWalletTransaction,
		handleOpenUpdateWalletTransactionModal,
		handleCloseUpdateWalletTransactionModal,
		handleSubmitUpdateWalletTransaction,
		setIsShowAddWalletTranModal,
	};
};